import React, { useEffect } from 'react';
import { Divider, MediaMatch, useViewport } from '@lamimed/pantheon';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { useCoupon } from 'hooks/useCoupon';
import { useGetProductBySlug } from 'hooks/useGetProductBySlug';
import { useSchedule } from 'hooks/useSchedule';

import { defaultMetatags } from 'utils/fallbackValues/metatags';
import { getQueryParamAsString } from 'utils/getQueryParamAsString';

import { ContentFade } from 'components/ContentFade';
import { footerImageOffset } from 'components/Footer/styles';
import { Header } from 'components/Header';
import ThumbnailDivider from 'components/ThumbnailDivider';
import { SectionWrapper } from 'components/wrappers/SectionWrapper';

import { CallToAction } from './containers/CallToAction';
import { CartDrawer } from './containers/CartDrawer';
import { CartProvider } from './containers/CartDrawer/useCart';
import { Conditions } from './containers/Conditions';
import { ProductHeroVariant } from './containers/HeroVariant';
import { MainText } from './containers/MainText';
import { ScheduleButton } from './containers/ScheduleButton';
import { Subproduct } from './containers/Subproduct';
import { useSubproduct } from './containers/Subproduct/useSubproduct';

import * as S from './styles';

export function isExam(category: string) {
  const whitelist = ['covid', 'esami-test'];

  return whitelist.includes(category);
}

const ProductTemplate: React.FC = () => {
  const router = useRouter();
  const { AppointmentInfo } = useSchedule();
  const { subproductData } = useSubproduct();
  const { greaterThan } = useViewport();

  const { data } = useGetProductBySlug(
    getQueryParamAsString(router.query.slug),
  );

  const { removeCoupon } = useCoupon();

  const returnEmptyIfUndefined = (dataString: string | undefined) => {
    return dataString ?? '';
  };

  const conditions = [
    {
      title: 'Preparazione',
      description: returnEmptyIfUndefined(data?.preparation),
      icon: 'time',
    },
    {
      title: 'Realizzazione',
      description: returnEmptyIfUndefined(data?.realization),
      icon: 'calendar',
    },
    {
      title: isExam(router.query.category as string) ? 'Referto' : 'Durata',
      description: returnEmptyIfUndefined(data?.result),
      icon: 'loading',
    },
  ];

  useEffect(() => {
    AppointmentInfo.clearState();

    removeCoupon();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CartProvider>
      <Head>
        <title key="title">{data?.metaTitle || defaultMetatags.title}</title>

        <meta
          name="description"
          content={data?.metaDescription || defaultMetatags.description}
          key="description"
        />
      </Head>

      {data && <CartDrawer productSlug={data.slug} />}

      <S.Wrapper>
        <MediaMatch greaterThan="tablet">
          <Header />
        </MediaMatch>

        <SectionWrapper
          paddingTop="none"
          paddingBottom={greaterThan('tablet') ? 'large' : 'xlarge'}
        >
          <ProductHeroVariant />
        </SectionWrapper>

        <SectionWrapper
          paddingTop="none"
          paddingBottom={data?.isTypology ? 'large' : 'none'}
        >
          {data?.isTypology && <Subproduct />}
        </SectionWrapper>

        <SectionWrapper
          paddingBottom={greaterThan('tablet') ? 'none' : 'xlarge'}
          paddingTop="none"
        >
          <MainText
            markdown={
              subproductData?.content || data?.additionalInfo?.markdown || ''
            }
          />
        </SectionWrapper>

        <SectionWrapper
          paddingTop="none"
          paddingBottom={greaterThan('tablet') ? 'xlarge' : 'none'}
        >
          {data?.similar && (
            <CallToAction
              title="Video visita"
              description="Risparmia fino al 50%"
              buttonText="Clicca qui"
              slug={returnEmptyIfUndefined(data?.similar?.slug)}
            />
          )}
        </SectionWrapper>

        <SectionWrapper
          paddingBottom={greaterThan('tablet') ? 'xlarge' : 'medium'}
          paddingTop={greaterThan('tablet') ? 'large' : 'none'}
        >
          <Conditions conditions={conditions} />
        </SectionWrapper>

        <MediaMatch lessThan="tablet">
          <Divider position="above" />
        </MediaMatch>

        <SectionWrapper
          paddingTop="none"
          paddingBottom={greaterThan('tablet') ? 'xxlarge' : 'xlarge'}
          additionalPadding={footerImageOffset}
        >
          <MediaMatch greaterThan="tablet">
            <ThumbnailDivider
              title={returnEmptyIfUndefined(data?.activityTitle)}
              description={data?.description}
              image={`/images/illustration/categories/${router.query.category}.png`}
            />

            <ScheduleButton />
          </MediaMatch>

          <MediaMatch lessThan="tablet">
            <ContentFade>
              <ScheduleButton />
            </ContentFade>
          </MediaMatch>
        </SectionWrapper>
      </S.Wrapper>
    </CartProvider>
  );
};

export default ProductTemplate;
